import React from "react";
import { BarChart, Bar, ResponsiveContainer, LabelList } from "recharts";
import { formatNumber } from "@/utils/numbers";
import UserAvatar from "@/components/controls/UserAvatar";
import { UserData } from "@/entities/users/types";
import { useWinRateBreadcrumbs } from "@/components/layouts/WinRate/WinRateBreadcrumbsContext";
import { salesPipelineColors } from "@/components/layouts/SalesPipeline/SalesColors";
import { indexOf } from "lodash";

interface Props {
  datasets: {
    name: string;
    value: number;
    userData?: UserData;
  }[];
  unit?: string;
}

const DrilldownComparisonChart: React.FC<Props> = ({
  datasets,
  unit = "%"
}) => {
  const { dynamicTableData } = useWinRateBreadcrumbs();

  const CustomLabel = (props: any) => {
    const { x, y, width, value: rawValue, name } = props;
    let label = "";
    const { value, modifier } = formatNumber(rawValue, 1, false, true);
    if (rawValue === 0) {
      return;
    }
    if (unit) {
      label = `${rawValue}${unit}`;
    } else {
      label = `${value}${modifier}`;
    }
    return (
      <text
        x={x + width / 2}
        y={y - 10}
        fill="var(--tertiary)"
        textAnchor="middle"
        dominantBaseline="middle"
        className="text-sm pb-6"
        data-test={`${name}-column-label`}
      >
        {label}
      </text>
    );
  };

  const CustomBar = (props: any) => {
    const { x, y, width, height, name } = props;
    const effectiveHeight = Math.max(height, 2);
    const adjustedY = y + (height - effectiveHeight);
    const cornerRadius = 5;
    return (
      <path
        d={`
        M ${x},${adjustedY + effectiveHeight}
        L ${x},${adjustedY + cornerRadius}
        Q ${x},${adjustedY} ${x + cornerRadius},${adjustedY}
        L ${x + width - cornerRadius},${adjustedY}
        Q ${x + width},${adjustedY} ${x + width},${adjustedY + cornerRadius}
        L ${x + width},${adjustedY + effectiveHeight}
        Z
      `}
        fill={`url(#${name})`}
      />
    );
  };

  return (
    <>
      <ResponsiveContainer className="max-h-20 h-full w-full">
        <BarChart data={datasets} margin={{ top: 20 }}>
          <defs>
            {dynamicTableData.map((userData) => (
              <linearGradient
                id={userData?.id}
                key={userData?.id}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="30%"
                  stopColor={
                    salesPipelineColors[
                      indexOf(dynamicTableData, userData) %
                        salesPipelineColors.length
                    ].stroke
                  }
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor={
                    salesPipelineColors[
                      indexOf(dynamicTableData, userData) %
                        salesPipelineColors.length
                    ].stroke
                  }
                  stopOpacity={0.5}
                />
              </linearGradient>
            ))}
          </defs>
          <Bar dataKey="value" shape={<CustomBar />} barSize={20}>
            <LabelList dataKey="value" content={<CustomLabel />} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="justify-around flex w-full mt-3">
        {datasets.map((dataset, index) => {
          return (
            <div key={index} className="flex flex-col justify-end items-center">
              <span className="transform size-5">
                <UserAvatar
                  size="M"
                  userData={dataset?.userData}
                />
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default DrilldownComparisonChart;
